import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Phone,
  User,
  Hash,
  Radio,
  Calendar,
  CircleDot,
  Pencil,
  XCircle,
} from "lucide-react";
import { formatDateTime, formatPhone } from "@/lib/format";
import type { Inbox } from "@/types/inbox";

interface ContactDetailPanelProps {
  inbox: Inbox;
  onClose?: () => void;
}

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

const statusLabels: Record<string, string> = {
  open: "Aberto",
  pending: "Pendente",
  resolved: "Resolvido",
};

function DetailRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value?: string | null;
}) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </p>
        <p className="mt-0.5 text-sm break-all">{value}</p>
      </div>
    </div>
  );
}

export function ContactDetailPanel({
  inbox,
  onClose,
}: ContactDetailPanelProps) {
  const contactName = inbox.contact_name || `Contato #${inbox.id_contact}`;

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 p-4">
        <div className="flex flex-col items-center gap-3 py-4">
          <Avatar className="h-16 w-16">
            <AvatarFallback className="text-lg font-semibold">
              {getInitials(contactName)}
            </AvatarFallback>
          </Avatar>
          <div className="text-center">
            <h3 className="text-sm font-semibold">{contactName}</h3>
            {inbox.contact_phone && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {formatPhone(inbox.contact_phone)}
              </p>
            )}
          </div>
        </div>

        <Separator />

        <Card size="sm">
          <CardHeader>
            <CardTitle className="text-xs">Contato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <DetailRow icon={User} label="Nome" value={inbox.contact_name} />
            {inbox.contact_phone &&
              inbox.contact_phone !== inbox.contact_identifier && (
                <DetailRow
                  icon={Phone}
                  label="Telefone"
                  value={formatPhone(inbox.contact_phone)}
                />
              )}
            <DetailRow
              icon={Hash}
              label="Identificador"
              value={inbox.contact_identifier}
            />
          </CardContent>
        </Card>

        <Card size="sm">
          <CardHeader>
            <CardTitle className="text-xs">Conversa</CardTitle>
          </CardHeader>
          <CardContent className="space-y-0">
            <DetailRow icon={Radio} label="Tipo" value={inbox.channel_type} />
            <DetailRow
              icon={Hash}
              label="Canal"
              value={
                inbox.channel_name || inbox.channel_identifier
              }
            />
            <DetailRow
              icon={Calendar}
              label="Criada em"
              value={formatDateTime(inbox.datetime_add)}
            />
            <DetailRow
              icon={CircleDot}
              label="Status"
              value={statusLabels[inbox.status] || inbox.status}
            />
          </CardContent>
        </Card>

        <Separator />

        <div className="flex flex-col gap-2">
          <Button variant="outline" size="sm" className="w-full" disabled>
            <Pencil className="mr-2 h-3.5 w-3.5" />
            Editar contato
          </Button>
          {onClose && (
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-destructive hover:text-destructive"
              onClick={onClose}
            >
              <XCircle className="mr-2 h-3.5 w-3.5" />
              Fechar conversa
            </Button>
          )}
        </div>
      </div>
    </ScrollArea>
  );
}
